//
import type { AxiosCollection, AxiosItem } from '@/types/Components/Response'
import type { Account } from '@/types/Models/Account'
import type { AccountStoreState } from '@/types/Store'
import { defineStore } from 'pinia'
import { CUSTOMER, STAFF } from '@/const/AccountTypes'
import { defineAbilitiesFor } from '@/services/CaslAbility'
import { catchErrors, none, resolveHomePage } from '@/services/Helpers'
import { useAuthStore } from '@/store/auth'
import { useCustomersStore } from '@/store/customers'
import AccountProxy from '@/proxies/AccountProxy'
import AccountTransformer from '@/transformers/AccountTransformer'

const createDefaultAccount = (): AccountStoreState['account'] => ({
  id: null,
  type: null,
  role: null,
  companyId: null,
  firstName: null,
  lastName: null,
  email: null,
  isScannerOnlyEnabled: false,
  isInventoryAvailable: false,
})

const createDefaultState = (): AccountStoreState => ({
  account: createDefaultAccount(),
  companyUsers: [],
})

export const useAccountStore = defineStore('account', {
  state: (): AccountStoreState => createDefaultState(),
  getters: {
    homePage: (state: AccountStoreState) => resolveHomePage(state.account),
    isStaff: (state: AccountStoreState) => state.account.type === STAFF,
    accountType: (state: AccountStoreState) => state.account.type,
    userId: (state: AccountStoreState) => state.account.id,
    companyId: (state: AccountStoreState) => state.account.companyId,
    isCustomer: (state: AccountStoreState) => state.account.type === CUSTOMER,
    ability: (state: AccountStoreState) => defineAbilitiesFor(state.account, useCustomersStore().customer),
    isLoggedIn: () => useAuthStore().authenticated,
  },
  actions: {
    resetAccount() {
      this.account = createDefaultAccount()
      this.companyUsers = []
    },
    me() {
      return new AccountProxy()
        .me()
        .then((response: AxiosItem<Account>) => {
          this.account = {
            ...createDefaultAccount(),
            ...AccountTransformer.fetch(response.data),
          }
          return response
        })
        .catch(() => useAuthStore().logout())
    },
    destroy(id: string) {
      return new AccountProxy().destroy(id).catch(catchErrors)
    },
    reset() {
      this.resetAccount()
    },
    fetchCompanyUsers(companyId: string) {
      return new AccountProxy({ companyId })
        .all()
        .then((response: AxiosCollection<Account>) => {
          this.companyUsers = AccountTransformer.fetchCollection(response.data)
          return response
        })
        .catch(none)
    },
    store({ companyId, data }: { companyId: string; data: Account }) {
      return new AccountProxy({ companyId }).create(data).catch(catchErrors)
    },
    update({ id, data }: { id: string; data: Account }) {
      return new AccountProxy().update(id, data)
    },
  },
})

export type AccountStore = ReturnType<typeof useAccountStore>
